import { Combobox as HeadlessCombobox, ComboboxInput, ComboboxOptions, ComboboxOption } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useState, useEffect } from "react";
import { Label } from "./label";

interface ComboboxProps {
    name: string;
    value: string | null;
    onChange: (value: string) => void;
    options: string[];
    label?: string;
    placeholder?: string;
    id?: string;
    defaultValue?: string;
}

function ClientOnlyCombobox({
    name,
    value,
    onChange,
    options,
    label,
    placeholder = "Search...",
    id,
    defaultValue = '',
}: ComboboxProps) {
    const [query, setQuery] = useState(defaultValue);

    const filteredOptions =
        query === ''
            ? options
            : options.filter((option) => {
                return option.toLowerCase().includes(query.toLowerCase());
            });

    return (
        <HeadlessCombobox immediate name={name} value={value} onChange={onChange}>
            <div className="relative">
                <ComboboxInput
                    id={id}
                    aria-label={label}
                    placeholder={placeholder}
                    autoComplete="off"
                    autoFocus={false}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid"
                    displayValue={(option: string) => option}
                    onChange={(event) => setQuery(event.target.value)}
                />
                {(query || value) && (
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        onClick={() => {
                            setQuery('');
                            onChange('');
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
            </div>
            <ComboboxOptions
                anchor="bottom start"
                transition
                className="origin-top border transition duration-200 ease-out empty:invisible data-[closed]:scale-95 data-[closed]:opacity-0"
            >
                {filteredOptions.map((option) => (
                    <ComboboxOption
                        key={option}
                        value={option}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid data-[focus]:bg-blue-500"
                    >
                        {({ focus, selected }) => (
                            <div className={clsx('group flex gap-2', focus)}>
                                {selected && <CheckIcon className="size-5" />}
                                {option}
                            </div>
                        )}
                    </ComboboxOption>
                ))}
            </ComboboxOptions>
        </HeadlessCombobox>
    );
}

export function Combobox(props: ComboboxProps) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <div className="w-full">
            {props.label && <Label htmlFor={props.id}>{props.label}</Label>}
            {mounted ? (
                <ClientOnlyCombobox {...props} />
            ) : (
                <div className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                    <input
                        type="text"
                        className="w-full bg-transparent outline-none"
                        placeholder={props.placeholder}
                        disabled
                    />
                </div>
            )}
        </div>
    );
} 